import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import { hasMenu } from '../utils/entitlements';
import '../css/admin-config.css';

function GlobalAlertSchedule({ onSaved }) {
  const [mode,    setMode]    = useState('ALWAYS_ON');
  const [start,   setStart]   = useState('00:30');
  const [end,     setEnd]     = useState('05:00');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    apiFetch('/settings/alert-schedule')
      .then(r => r.json())
      .then(d => {
        const raw = (d.mode || '').toUpperCase().replace(/-/g, '_');
        const norm = raw.includes('TIME') || raw.includes('WINDOW') ? 'TIME_BASED'
                   : raw === 'OFF' ? 'OFF'
                   : 'ALWAYS_ON';
        setMode(norm);
        setStart(d.start || '00:30');
        setEnd(d.end || '05:00');
        onSaved({ mode: norm, start: d.start || '00:30', end: d.end || '05:00' });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiFetch('/settings/alert-schedule', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, start, end }),
      });
      if (r.ok) onSaved({ mode, start, end });
      setMsg(r.ok ? { ok: true, text: '✓ Saved' } : { ok: false, text: 'Failed to save' });
    } catch { setMsg({ ok: false, text: 'Cannot reach server' }); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  if (loading) return <div className="adm-state" style={{ minHeight: 80 }}><div className="adm-spinner" /></div>;

  return (
    <div className="sched-subsection">
      <div className="sched-sub-title">🌐 Global Schedule</div>

      <div className="sched-mode-row">
        {[
          ['ALWAYS_ON',  '🟢', 'Always On'],
          ['OFF',        '🔴', 'Off'],
          ['TIME_BASED', '⏰', 'Time Window'],
        ].map(([val, icon, label]) => (
          <button key={val}
            className={`sched-mode-btn${mode === val ? ' active' : ''}`}
            onClick={() => setMode(val)}>
            {mode === val && <span style={{ fontSize: 11 }}>✓</span>}
            <span>{icon}</span>
            <span>{label}</span>
          </button>
        ))}
      </div>

      {mode === 'TIME_BASED' && (
        <div className="sched-time-row">
          <div className="adm-field">
            <label>Start Time</label>
            <input className="adm-input" type="time" value={start} onChange={e => setStart(e.target.value)} />
          </div>
          <span className="sched-arrow">→</span>
          <div className="adm-field">
            <label>End Time</label>
            <input className="adm-input" type="time" value={end} onChange={e => setEnd(e.target.value)} />
          </div>
        </div>
      )}

      <div className="sched-save-row">
        <button className="adm-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Schedule'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function DeviceChannelCard({ ch, onSaved }) {
  const canSetUp = hasMenu('admin-config') || hasMenu('guided-setup');
  const [local,   setLocal]   = useState({ on_alert: ch.on_alert ?? false, alert_schedule: ch.alert_schedule, fixed_schedule: ch.fixed_schedule });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  const sched    = local.alert_schedule;
  const isTimed  = sched && typeof sched === 'object';
  const fs       = local.fixed_schedule;
  const hasFixed = fs && typeof fs === 'object';

  const set = patch => setLocal(prev => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiFetch(`/ui/devices/relay/channels/${ch.channel}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(local),
      });
      if (r.ok) {
        setMsg({ ok: true, text: '✓ Saved' });
        onSaved();
      } else {
        setMsg({ ok: false, text: 'Failed to save' });
      }
    } catch { setMsg({ ok: false, text: 'Cannot reach server' }); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  return (
    <div className="adm-user-card">
      <div className="adm-user-card-top">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="adm-user-card-name">{ch.name || `Channel ${ch.channel}`}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>CH {ch.channel}</span>
        </div>
        {/* Whether a siren answers alerts at all is setup, not a household choice —
            the box refuses it from an End User (PRVSK-141). When it sounds is theirs. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                        cursor: canSetUp ? 'pointer' : 'default' }}
               title={canSetUp ? undefined : 'Only your installer or site admin can change this'}>
          <input type="checkbox" checked={local.on_alert} disabled={!canSetUp}
            onChange={e => set({ on_alert: e.target.checked })} />
          On Alert
        </label>
      </div>

      <div className="adm-ch-card-body">
        <div className="adm-ch-row">
          <span className="adm-ch-label">Alert Schedule</span>
          {local.on_alert ? (
            <div className="adm-sched-wrap">
              <select className="adm-sched-mode"
                value={isTimed ? 'timed' : 'always'}
                onChange={e => set({ alert_schedule: e.target.value === 'always' ? 'always' : { start: '22:00', end: '06:00' } })}>
                <option value="always">Always</option>
                <option value="timed">Timed</option>
              </select>
              {isTimed && (
                <div className="adm-sched-times">
                  <input type="time" className="adm-sched-time" value={sched.start || '22:00'}
                    onChange={e => set({ alert_schedule: { ...sched, start: e.target.value } })} />
                  <span className="adm-sched-sep">→</span>
                  <input type="time" className="adm-sched-time" value={sched.end || '06:00'}
                    onChange={e => set({ alert_schedule: { ...sched, end: e.target.value } })} />
                </div>
              )}
            </div>
          ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
        </div>

        <div className="adm-ch-row">
          <span className="adm-ch-label">Fixed Schedule</span>
          <div className="adm-sched-wrap">
            <select className="adm-sched-mode"
              value={hasFixed ? 'on' : 'off'}
              onChange={e => set({ fixed_schedule: e.target.value === 'on' ? { start: '07:00', end: '19:00' } : null })}>
              <option value="off">Off</option>
              <option value="on">Scheduled</option>
            </select>
            {hasFixed && (
              <div className="adm-sched-times">
                <input type="time" className="adm-sched-time" value={fs.start || '07:00'}
                  onChange={e => set({ fixed_schedule: { ...fs, start: e.target.value } })} />
                <span className="adm-sched-sep">→</span>
                <input type="time" className="adm-sched-time" value={fs.end || '19:00'}
                  onChange={e => set({ fixed_schedule: { ...fs, end: e.target.value } })} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="sched-save-row" style={{ marginTop: 12 }}>
        <button className="adm-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function CameraScheduleCard({ cam, idx, onSaved, globalSched }) {
  const [mode,  setMode]  = useState(cam.alert_time_mode || 'INHERIT');
  const [start, setStart] = useState(cam.alert_time?.start || '00:30');
  const [end,   setEnd]   = useState(cam.alert_time?.end   || '23:59');
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiFetch(`/cameras/${idx}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_time_mode: mode, alert_time: { start, end } }),
      });
      if (r.ok) { setMsg({ ok: true, text: '✓ Saved' }); onSaved(); }
      else       { setMsg({ ok: false, text: 'Failed to save' }); }
    } catch { setMsg({ ok: false, text: 'Cannot reach server' }); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  const globalDesc = globalSched
    ? globalSched.mode === 'TIME_BASED'
      ? `Global: Time Window ${globalSched.start} – ${globalSched.end}`
      : globalSched.mode === 'OFF' ? 'Global: Off' : 'Global: Always On'
    : 'Global: —';

  return (
    <div className="adm-user-card">
      <div className="adm-user-card-top">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="adm-user-card-name">{cam.name || `Camera ${idx + 1}`}</span>
          {mode === 'INHERIT' && (
            <span style={{ fontSize: 11, color: 'var(--primary, #1f8ef1)', fontWeight: 500 }}>
              🌐 {globalDesc}
            </span>
          )}
        </div>
      </div>

      <div className="sched-cam-modes">
        {[['INHERIT', '🌐', 'Follow Global'], ['ALWAYS_ON', '🟢', 'Always On'], ['OFF', '🔴', 'Off'], ['CUSTOM', '⏰', 'Custom']].map(([val, icon, label]) => (
          <button key={val}
            className={`sched-cam-mode-btn${mode === val ? ' active' : ''}`}
            onClick={() => setMode(val)}>
            {icon} {label}
          </button>
        ))}
      </div>

      {mode === 'CUSTOM' && (
        <div className="sched-time-row" style={{ marginTop: 10 }}>
          <div className="adm-field">
            <label>Start Time</label>
            <input className="adm-input" type="time" value={start} onChange={e => setStart(e.target.value)} />
          </div>
          <span className="sched-arrow">→</span>
          <div className="adm-field">
            <label>End Time</label>
            <input className="adm-input" type="time" value={end} onChange={e => setEnd(e.target.value)} />
          </div>
        </div>
      )}

      <div className="sched-save-row" style={{ marginTop: 12 }}>
        <button className="adm-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function CameraSchedules({ globalSched }) {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res  = await apiFetch('/ui/cameras');
      const data = await res.json();
      setCameras(data.cameras || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="adm-state" style={{ minHeight: 80 }}><div className="adm-spinner" /></div>;

  if (cameras.length === 0) return null;

  return (
    <div className="sched-subsection">
      <div className="sched-sub-title">📷 Camera Schedules</div>
      <div className="adm-card-list">
        {cameras.map((cam, idx) => (
          <CameraScheduleCard key={idx} cam={cam} idx={idx} onSaved={load} globalSched={globalSched} />
        ))}
      </div>
    </div>
  );
}

function DeviceSchedules() {
  const [channels, setChannels] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    try {
      const res  = await apiFetch('/ui/devices/status');
      const data = await res.json();
      setChannels(data.relay_channels || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="adm-state" style={{ minHeight: 80 }}><div className="adm-spinner" /></div>;

  if (channels.length === 0) return (
    <div className="sched-section">
      <div className="sched-section-hdr">
        <span className="sched-section-icon">🔔</span>
        <div>
          <div className="sched-section-title">Device Schedules</div>
          <div className="sched-section-sub">No relay channels configured</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="sched-section">
      <div className="sched-section-hdr">
        <span className="sched-section-icon">🔔</span>
        <div>
          <div className="sched-section-title">Device Schedules</div>
          <div className="sched-section-sub">Per-device alert and fixed schedules</div>
        </div>
      </div>

      <div className="adm-card-list">
        {channels.map(ch => (
          <DeviceChannelCard key={ch.channel} ch={ch} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const [globalSched, setGlobalSched] = useState(null);

  return (
    <div className="sched-page">
      <div className="sched-section">
        <div className="sched-section-hdr">
          <span className="sched-section-icon">📋</span>
          <div>
            <div className="sched-section-title">Alert Schedule</div>
            <div className="sched-section-sub">Control when alerts fire globally and per camera</div>
          </div>
        </div>
        <GlobalAlertSchedule onSaved={setGlobalSched} />
        <CameraSchedules globalSched={globalSched} />
      </div>
      <DeviceSchedules />
    </div>
  );
}
